import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, FileText, RefreshCw } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState, PageHeader, StatusPill } from "@/components/uiext";

function fmtTime(s) {
  if (!s) return "—";
  return new Date(s * 1000).toLocaleString();
}
function fmtDur(d) {
  if (!d) return "—";
  const s = Math.round(Number(d));
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

export default function CDRsPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [direction, setDirection] = useState("");
  const [cause, setCause] = useState("");
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState({});
  const [trunks, setTrunks] = useState({});

  const load = async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (direction) qs.set("direction", direction);
    if (cause) qs.set("release_cause", cause);
    qs.set("limit", "200");
    try {
      const r = await api.get(`/cdrs?${qs.toString()}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    Promise.all([api.get("/tenants"), api.get("/trunks")]).then(([tr, tk]) => {
      const tMap = {};
      tr.data.forEach((t) => (tMap[t.id] = t));
      setTenants(tMap);
      const kMap = {};
      tk.data.forEach((t) => (kMap[t.id] = t));
      setTrunks(kMap);
    });
    load();
  }, []);
  useEffect(() => {
    load();
  }, [direction, cause]);

  const exportCsv = () => {
    const token = localStorage.getItem("elswitch_token");
    const qs = new URLSearchParams();
    if (direction) qs.set("direction", direction);
    const url = `${API_BASE}/cdrs/export.csv?${qs.toString()}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "cdrs.csv";
        a.click();
      });
  };

  return (
    <div data-testid="page-cdrs">
      <PageHeader
        title={t("pages.cdrs.title")}
        subtitle={`${total.toLocaleString()} CDRs — traffic analytics only (no billing).`}
        icon={FileText}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" onClick={load} data-testid="cdrs-refresh">
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
            <Button onClick={exportCsv} data-testid="cdrs-export-csv">
              <Download className="h-4 w-4 mr-1" /> Export CSV
            </Button>
          </div>
        }
      />
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div>
          <Label className="text-xs">Direction</Label>
          <select
            className="flex h-9 w-[160px] rounded-md border border-input bg-background px-3 text-sm"
            value={direction}
            onChange={(e) => setDirection(e.target.value)}
          >
            <option value="">All</option>
            <option value="outbound">Outbound</option>
            <option value="inbound">Inbound</option>
          </select>
        </div>
        <div>
          <Label className="text-xs">Release cause</Label>
          <Input
            className="w-[160px]"
            placeholder="e.g. BYE or 486"
            value={cause}
            onChange={(e) => setCause(e.target.value)}
          />
        </div>
      </div>
      {items.length === 0 ? (
        <EmptyState
          title={loading ? t("pages.cdrs.loading", "Loading…") : t("pages.cdrs.noCdrs", "No CDRs yet")}
          description={t("pages.cdrs.noCdrsDesc", "Once calls start flowing through the engine, their details will appear here.")}
        />
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader className="sticky-table-head">
              <TableRow>
                <TableHead>{t("pages.cdrs.cols.started", "Started")}</TableHead>
                <TableHead>{t("pages.cdrs.cols.duration", "Duration")}</TableHead>
                <TableHead>{t("pages.cdrs.cols.customer", "Customer")}</TableHead>
                <TableHead>{t("pages.cdrs.cols.trunk", "Trunk")}</TableHead>
                <TableHead>{t("pages.cdrs.cols.from", "From")}</TableHead>
                <TableHead title={t("pages.cdrs.cols.sentCidTip", "The carrier-facing From URI the engine actually sent on outbound calls (after CID rewrite). Blank for inbound calls.")}>
                  {t("pages.cdrs.cols.sentCid", "Sent CID →")}
                </TableHead>
                <TableHead>{t("pages.cdrs.cols.to", "To")}</TableHead>
                <TableHead>{t("pages.cdrs.cols.direction", "Dir")}</TableHead>
                <TableHead>{t("pages.cdrs.cols.cause", "Cause")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.call_id} className="row-hover" data-testid={`cdr-row-${c.call_id}`}>
                  <TableCell className="text-xs">{fmtTime(c.start_time)}</TableCell>
                  <TableCell className="text-xs font-mono">{fmtDur(c.duration)}</TableCell>
                  <TableCell className="text-sm">
                    <span title={c.tenant_id || ""}>
                      {c.tenant_name || tenants[c.tenant_id]?.name || (c.tenant_id ? `${c.tenant_id.slice(0, 8)}…` : "—")}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">
                    <span title={c.trunk_id || ""}>
                      {c.trunk_name || trunks[c.trunk_id]?.name || (c.trunk_id ? `${c.trunk_id.slice(0, 8)}…` : "—")}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{c.from}</TableCell>
                  <TableCell
                    className="font-mono text-xs text-muted-foreground"
                    data-testid={`cdr-bfrom-${c.call_id}`}
                  >
                    {c.b_from_uri || (c.direction === "outbound" ? "—" : "")}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{c.to}</TableCell>
                  <TableCell>
                    <StatusPill kind="info">{c.direction}</StatusPill>
                  </TableCell>
                  <TableCell>
                    <StatusPill
                      kind={c.release_cause === "BYE" ? "ok" : "warn"}
                    >
                      {c.release_cause || "—"}
                    </StatusPill>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
